import React, { useState } from 'react';
import { Checkbox, Box, Typography, MenuItem, Select, ListItemText } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTheme } from '@mui/material/styles';
import {
  buildControlMenuProps,
  getControlTokens,
} from '../common/controls';

const FilterTableFields = ({
  label,
  selectedColumns,
  handleColumnsChange,
  allColDefs,
  setSelectedColumns,
  getColumnLabel = (colDef) => colDef.headerName || colDef.field,
  menuMinWidth,
  wrapLabels = false,
  controlContext = 'filterBar',
  menuContext = controlContext,
}) => {
  const theme = useTheme();
  const tokens = getControlTokens(theme, controlContext);
  const [expandedGroups, setExpandedGroups] = useState({});
 
  // Group columns by their 'group' property
  const groupedColDefs = allColDefs.reduce((acc, colDef) => {
    const group = colDef.group || 'Ungrouped'; // Default group if no group exists
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(colDef);
    return acc;
  }, {});

  const handleExpandToggle = (group) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };
  
  const handleCheckboxChange = (colDef) => (e) => {
    const newValue = e.target.checked;
    const updatedLocalSelectedColumns = newValue
      ? [...selectedColumns, colDef.field]
      : selectedColumns.filter((field) => field !== colDef.field);

    setSelectedColumns(updatedLocalSelectedColumns);
    handleColumnsChange({ target: { value: updatedLocalSelectedColumns } });
  };

  const handleMenuItemClick = (colDef) => {
    const isChecked = selectedColumns.includes(colDef.field);
    handleCheckboxChange(colDef)({ target: { checked: !isChecked } });
  };

  const handleGroupCheckboxChange = (group) => () => {
    const allFields = groupedColDefs[group].map(colDef => colDef.field);
    const allSelected = allFields.every(field => selectedColumns.includes(field));

    if (allSelected) {
      // If all items are selected, uncheck all items in this group, but keep "Company Name" checked
      const updatedColumns = selectedColumns.filter(field => !allFields.includes(field) || field === 'Company Name');
      setSelectedColumns(updatedColumns);
      handleColumnsChange({ target: { value: updatedColumns } });
    } else {
      // Check all items in this group if any are unchecked
      const updatedColumns = [...new Set([...selectedColumns, ...allFields])];
      setSelectedColumns(updatedColumns);
      handleColumnsChange({ target: { value: updatedColumns } });
    }
  };

  return (
    <Box
      size="small"
      sx={{
        height: tokens.height,
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        minWidth: 0,
        marginTop: '0px',
        maxWidth: '100%',
        flex: 1,
      }}
    >
      <Select
        size="small"
        sx={{
          backgroundColor: tokens.surface,
          width: '100%',
          height: tokens.height,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
        }}
        multiple
        value={selectedColumns}
        onChange={handleColumnsChange}
        renderValue={() => (
          <Typography
            variant="body2"
            sx={{
              fontSize: tokens.fontSize,
              display: 'flex',
              alignItems: 'center',
              pt: '3px',
              color: theme.palette.text.secondary,
            }}
          >
            {label}
          </Typography>
        )}
        MenuProps={buildControlMenuProps(theme, menuContext, {
          PaperProps: {
            elevation: 4,
            sx: {
              maxHeight: '65vh',
              minWidth: menuMinWidth,
            },
          },
        })}
      >
        {/* Top-level ungrouped items */}
        {groupedColDefs['Ungrouped'] &&
          groupedColDefs['Ungrouped'].map((colDef) => {
            const isSelected = selectedColumns.includes(colDef.field);
            return (
              <MenuItem
                key={colDef.field}
                value={colDef.field}
                disabled={colDef.field === 'Company Name'}
                sx={{
                  minHeight: wrapLabels ? '36px' : '26px',
                  height: wrapLabels ? 'auto' : '26px',
                  py: 0.25,
                  px: 1.5,
                  justifyContent: 'space-between',
                  backgroundColor: isSelected
                    ? theme.palette.action.selected
                    : 'transparent',
                  borderLeft: isSelected
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                  opacity: colDef.field === 'Company Name' ? 0.5 : 1,
                  transition: 'background-color 0.1s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                onClick={() => {
                  if (colDef.field === 'Company Name') return;
                  handleCheckboxChange(colDef)({ target: { checked: !isSelected } });
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      title={getColumnLabel(colDef)}
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '12px',
                        lineHeight: 1.2,
                        whiteSpace: wrapLabels ? 'normal' : 'nowrap',
                        overflowWrap: wrapLabels ? 'anywhere' : 'normal',
                      }}
                      variant="body2"
                    >
                      {getColumnLabel(colDef)}
                    </Typography>
                  }
                  sx={{ my: 0 }}
                />
                <Checkbox
                  size="small"
                  checked={isSelected || colDef.field === 'Company Name'}
                  disabled={colDef.field === 'Company Name'}
                  sx={{
                    p: 0,
                    ml: 0.5,
                    opacity: colDef.field === 'Company Name' ? 0.5 : 1,
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                  }}
                />
              </MenuItem>
            );
          })}

        {/* Grouped items */}
        {Object.keys(groupedColDefs)
          .filter((group) => group !== 'Ungrouped')
          .map((group, groupIndex, filteredGroups) => {
            const allFields = groupedColDefs[group].map(colDef => colDef.field);
            const allSelected = allFields.every(field => selectedColumns.includes(field));
            const anySelected = allFields.some(field => selectedColumns.includes(field));
            const isIndeterminate = !allSelected && anySelected;

            return [
              <Box
                key={`${group}-header`}
                role="button"
                aria-expanded={expandedGroups[group]}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleExpandToggle(group);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: '32px',
                  px: 1.5,
                  cursor: 'pointer',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(0, 0, 0, 0.02)',
                  borderTop: groupIndex > 0
                    ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`
                    : 'none',
                  transition: 'background-color 0.1s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.06)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleExpandToggle(group);
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {expandedGroups[group] ? (
                    <ExpandLessIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                    }}
                  >
                    {group}
                  </Typography>
                </Box>
                <Checkbox
                  indeterminate={isIndeterminate}
                  size="small"
                  checked={allSelected}
                  onChange={handleGroupCheckboxChange(group)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    p: 0,
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                  }}
                />
              </Box>,
              expandedGroups[group] &&
              groupedColDefs[group].map((colDef) => {
                const isSelected = selectedColumns.includes(colDef.field);
                return (
                  <MenuItem
                    key={colDef.field}
                    disabled={colDef.field === 'Company Name'}
                    sx={{
                      minHeight: wrapLabels ? '36px' : '26px',
                      height: wrapLabels ? 'auto' : '26px',
                      py: 0.25,
                      pl: 3.5,
                      pr: 1.5,
                      justifyContent: 'space-between',
                      backgroundColor: isSelected
                        ? theme.palette.mode === 'dark'
                          ? 'rgba(145, 166, 205, 0.16)'
                          : 'rgba(63, 100, 168, 0.16)'
                        : 'transparent',
                      borderLeft: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : '2px solid transparent',
                      opacity: colDef.field === 'Company Name' ? 0.5 : 1,
                      transition: 'background-color 0.1s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    value={colDef.field}
                    onClick={() => handleMenuItemClick(colDef)}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          title={getColumnLabel(colDef)}
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '12px',
                            lineHeight: 1.2,
                            whiteSpace: wrapLabels ? 'normal' : 'nowrap',
                            overflowWrap: wrapLabels ? 'anywhere' : 'normal',
                          }}
                          variant="body2"
                        >
                          {getColumnLabel(colDef)}
                        </Typography>
                      }
                      sx={{ my: 0 }}
                    />
                    <Checkbox
                      size="small"
                      checked={isSelected || colDef.field === 'Company Name'}
                      disabled={colDef.field === 'Company Name'}
                      sx={{
                        p: 0,
                        ml: 0.5,
                        opacity: colDef.field === 'Company Name' ? 0.5 : 1,
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                      }}
                    />
                  </MenuItem>
                );
              })
            ];
          })}
      </Select>
    </Box>
  );
};

export default FilterTableFields;
